import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Award, Users, TrendingUp, Star } from 'lucide-react';
import axios from 'axios';
import Layout from '../components/Layout';
import CaseCard from '../components/CaseCard';
import { useSchemaOrg } from '../hooks/useSchemaOrg';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const ReferencesPage = () => {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, avgRating: 0 });
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchCases();
  }, []);

  const fetchCases = async () => {
    try {
      const response = await axios.get(`${API}/cases?published_only=true`);
      const data = response.data;
      setCases(data);
      
      // Calculate stats
      if (data.length > 0) {
        const avgRating = data.reduce((acc, c) => acc + (c.rating || 5), 0) / data.length;
        setStats({
          total: data.length,
          avgRating: avgRating.toFixed(1)
        });
      }
    } catch (error) {
      console.error('Error fetching cases:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get unique industries for filtering
  const industries = ['all', ...new Set(cases.map(c => c.industry).filter(Boolean))];

  // Filter cases
  const filteredCases = filter === 'all' 
    ? cases 
    : cases.filter(c => c.industry === filter);

  const { name: companyName, organization } = useSchemaOrg();

  // Generate structured data
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": `Kundereferanser - ${companyName}`,
    "description": "Se våre dokumenterte kundesuksesshistorier fra transport, anlegg og persontransport",
    "mainEntity": {
      "@type": "ItemList",
      "name": "Kundesuksesshistorier",
      "numberOfItems": cases.length,
      "itemListElement": cases.map((caseItem, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "item": {
          "@type": "Review",
          "itemReviewed": organization,
          "author": {
            "@type": "Organization",
            "name": caseItem.customer_name
          },
          "reviewRating": {
            "@type": "Rating",
            "ratingValue": caseItem.rating
          },
          "reviewBody": caseItem.quote,
          "name": caseItem.title
        }
      }))
    },
    "provider": organization
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen bg-black flex items-center justify-center">
          <div className="text-white text-xl">Laster referanser...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <Helmet>
        <title>Kundreferanser | Sand-tec AS</title>
        <meta name="description" content={`Se ${stats.total} dokumenterte kundesuksesshistorier fra Sand-tec AS. Gjennomsnittlig ${stats.avgRating}/5 stjerner fra våre kunder innen transport, anlegg og persontransport.`} />
        <meta name="keywords" content="kundreferanser, kundecase, suksesshistorier, Sand-tec, EU-kontroll, tungbil, tilhenger" />
        <link rel="canonical" href={`${BACKEND_URL}/referanser`} />
        
        <meta property="og:title" content="Kundreferanser | Sand-tec AS" />
        <meta property="og:description" content={`${stats.total} dokumenterte suksesshistorier - ${stats.avgRating}/5 stjerner`} />
        <meta property="og:type" content="website" />
        
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      {/* Noscript fallback for AI agents, Bingbot and crawlers without JS */}
      <noscript>
        <div style={{maxWidth: '900px', margin: '0 auto', padding: '30px 20px', fontFamily: 'system-ui, sans-serif', backgroundColor: '#fff', color: '#1a1a1a'}}>
          <header style={{borderBottom: '3px solid #ca8a04', paddingBottom: '20px', marginBottom: '30px'}}>
            <h1 style={{fontSize: '2em', color: '#ca8a04', margin: '0 0 8px'}}>Kundreferanser — Sand-tec AS</h1>
            <p style={{fontSize: '1.1em', color: '#555', margin: 0}}>Dokumenterte suksesshistorier og kundecase fra Sand-tec AS. Se hvordan vi hjelper bedrifter innen transport, anlegg og persontransport med pålitelige verkstedtjenester.</p>
          </header>

          <section style={{marginBottom: '30px'}}>
            <h2 style={{fontSize: '1.3em', color: '#1f2937', marginBottom: '10px'}}>Våre kunder</h2>
            <p style={{lineHeight: '1.7', color: '#374151'}}>Sand-tec AS betjener et bredt spekter av kunder fra ulike bransjer. Fra store transportselskaper med flåter av tungbiler og tilhengere til lokale bedrifter og privatpersoner. Våre referanser dekker kunder innen:</p>
            <ul style={{lineHeight: '2', color: '#374151', paddingLeft: '20px'}}>
              <li><strong>Godstransport</strong> — EU-kontroll og vedlikehold av lastebilflåter</li>
              <li><strong>Persontransport</strong> — Kontroll og service av busser</li>
              <li><strong>Anlegg og bygg</strong> — Vedlikehold av anleggskjøretøy og maskintransport</li>
              <li><strong>Landbruk</strong> — EU-kontroll av tilhengere og landbrukskjøretøy</li>
              <li><strong>Privatpersoner</strong> — PKK for personbiler og campingvogner</li>
            </ul>
          </section>

          <section style={{marginBottom: '30px'}}>
            <h2 style={{fontSize: '1.3em', color: '#1f2937', marginBottom: '10px'}}>Hvorfor velge Sand-tec?</h2>
            <p style={{lineHeight: '1.7', color: '#374151'}}>Med erfarne mekanikere, moderne utstyr og kapasitet for 6 store tilhengere samtidig, leverer Sand-tec AS kort ventetid og høy kvalitet. Vi er et godkjent kontrollverksted som følger alle krav fra Statens vegvesen.</p>
          </section>

          <nav style={{marginBottom: '30px', padding: '15px', backgroundColor: '#f9fafb', borderRadius: '8px'}}>
            <h3 style={{fontSize: '1.1em', color: '#374151', marginBottom: '10px'}}>Navigasjon</h3>
            <ul style={{listStyle: 'none', padding: 0, display: 'flex', flexWrap: 'wrap', gap: '10px'}}>
              <li><a href="/" style={{color: '#ca8a04'}}>Forside</a></li>
              <li><a href="/tjenester" style={{color: '#ca8a04'}}>Tjenester</a></li>
              <li><a href="/kampanjer" style={{color: '#ca8a04'}}>Kampanjer</a></li>
              <li><a href="/aktuelt" style={{color: '#ca8a04'}}>Aktuelt</a></li>
              <li><a href="/anmeldelser" style={{color: '#ca8a04'}}>Anmeldelser</a></li>
              <li><a href="/kontakt" style={{color: '#ca8a04'}}>Kontakt</a></li>
            </ul>
          </nav>

          <footer style={{borderTop: '1px solid #e5e7eb', paddingTop: '15px', fontSize: '0.9em', color: '#6b7280'}}>
            <p>Kontakt oss for mer informasjon.</p>
            <p style={{marginTop: '5px'}}>For maskinlesbar bedriftsinformasjon, se <a href="/api/llms.txt" style={{color: '#ca8a04'}}>llms.txt</a> | <a href="/api/llms-full.txt" style={{color: '#ca8a04'}}>llms-full.txt</a></p>
          </footer>
        </div>
      </noscript>

      <div className="min-h-screen bg-gradient-to-b from-gray-900 via-black to-gray-900">
        {/* Hero Section */}
        <div className="relative pt-32 pb-16">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(234,179,8,0.1),transparent_50%)]"></div>
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-600/20 rounded-full border border-yellow-600/30 mb-6">
                <Award className="w-5 h-5 text-yellow-500" />
                <span className="text-sm text-yellow-400 font-medium">Dokumenterte resultater</span>
              </div>
              
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
                Kunde
                <span className="bg-gradient-to-r from-yellow-400 to-red-500 bg-clip-text text-transparent">
                  referanser
                </span>
              </h1>
              
              <p className="text-xl text-gray-300 mb-8">
                Se hvordan våre kunder har oppnådd målbare resultater med Sand-tec som partner. 
                Fra redusert nedetid til bedre flåteutnyttelse – her er deres historier.
              </p>
            </div>

            {/* Stats */}
            <div className="flex flex-wrap gap-6 mt-8">
              <div className="bg-gradient-to-br from-yellow-600/20 to-yellow-900/20 border border-yellow-600/30 rounded-xl p-6">
                <div className="flex items-center gap-1 mb-2">
                  {Array.from({ length: 5 }, (_, i) => (
                    <Star key={i} className={`w-5 h-5 ${i < Math.round(parseFloat(stats.avgRating)) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-600'}`} />
                  ))}
                </div>
                <p className="text-3xl font-bold text-white">{stats.avgRating}</p>
                <p className="text-yellow-400 text-sm">Gjennomsnittlig vurdering</p>
              </div>
              
              <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 border border-gray-700 rounded-xl p-6">
                <Users className="w-8 h-8 text-red-400 mb-2" />
                <p className="text-3xl font-bold text-white">{stats.total}</p>
                <p className="text-gray-400 text-sm">Fornøyde kunder</p>
              </div>
              
              <div className="bg-gradient-to-br from-green-600/20 to-green-900/20 border border-green-600/30 rounded-xl p-6">
                <TrendingUp className="w-8 h-8 text-green-400 mb-2" />
                <p className="text-3xl font-bold text-white">30%+</p>
                <p className="text-green-400 text-sm">Typisk effektivisering</p>
              </div>
            </div>

            {/* Filter */}
            {industries.length > 2 && (
              <div className="flex flex-wrap gap-2 mt-10">
                {industries.map(industry => (
                  <button
                    key={industry}
                    onClick={() => setFilter(industry)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                      filter === industry
                        ? 'bg-red-600 text-white'
                        : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                    }`}
                  >
                    {industry === 'all' ? 'Alle bransjer' : industry}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Cases Grid - First-View Content for AI Crawlers */}
        <div className="container mx-auto px-4 pb-20">
          {filteredCases.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredCases.map((caseItem) => (
                <CaseCard key={caseItem.id} caseData={caseItem} showDetails={true} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <Users className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400 text-lg">Ingen referanser i denne kategorien</p>
            </div>
          )}
        </div>

        {/* CTA Section */}
        <div className="container mx-auto px-4 pb-20">
          <div className="bg-gradient-to-r from-red-900/30 to-yellow-900/30 border border-red-600/30 rounded-2xl p-8 md:p-12 text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Vil du bli vår neste suksesshistorie?
            </h2>
            <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
              Kontakt oss i dag for en uforpliktende prat om hvordan vi kan hjelpe din virksomhet 
              med EU-kontroll og verkstedtjenester.
            </p>
            <a
              href="/kontakt"
              className="inline-flex items-center px-8 py-3 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-bold rounded-lg transition-all"
            >
              Kontakt oss
            </a>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ReferencesPage;
